import { Badge } from "@/components/ui/badge";
import { Zap, CheckCircle2, ShieldOff } from 'lucide-react';
import type { InsightEvent } from "@/hooks/useInsightEvents";

interface InsightEventRowProps {
  event: InsightEvent;
  side: 'home' | 'away';
}

interface InsightVarDisallowedRowProps extends InsightEventRowProps {
  varDetail: string;
}

function formatStatLabel(stat: 'sot' | 'corners'): string {
  return stat === 'sot' ? 'shot on target' : 'corner';
}

function formatStatLabelPlural(stat: 'sot' | 'corners'): string {
  return stat === 'sot' ? 'shots on target' : 'corners';
}

const VAR_MESSAGES = [
  (team: string) => `SG7 called the goal for ${team}, but even we can't overrule VAR. Football, eh?`,
  (team: string) => `SG7 saw it coming — shame VAR had other plans for ${team}.`,
  (team: string) => `SG7 nailed the prediction for ${team}, but VAR had the final say.`,
  (team: string) => `${team} scored, SG7 celebrated... then VAR said no. Can't win them all.`,
  (team: string) => `SG7 predicted the goal. VAR cancelled it. We're good, not miracle workers.`,
];

export function InsightAlertRow({ event, side }: InsightEventRowProps) {
  const isAway = side === 'away';
  const ordinal = event.count
    ? `${event.count}${event.count === 1 ? 'st' : event.count === 2 ? 'nd' : event.count === 3 ? 'rd' : 'th'}`
    : '';

  const message = event.count && event.ratio
    ? `${event.team_name} recorded their ${ordinal} ${formatStatLabel(event.stat_type)}. They average 1 goal every ${event.ratio.toFixed(2)} ${formatStatLabelPlural(event.stat_type)}.`
    : `SG7 detected a strong ${formatStatLabelPlural(event.stat_type)} trend for ${event.team_name}.`;

  return (
    <div className={`relative flex items-start gap-2.5 py-3.5 px-3 overflow-hidden ${isAway ? 'flex-row-reverse' : ''}`}>
      {/* Gradient side border */}
      <div className={`absolute top-0 bottom-0 w-[3px] bg-gradient-to-b from-amber-400 to-amber-600 ${isAway ? 'right-0' : 'left-0'}`} />
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-500/[0.08] via-amber-500/[0.04] to-transparent pointer-events-none" />
      {/* Gradient bottom separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-amber-500/30 via-amber-500/15 to-transparent" />

      <Badge className="relative flex-shrink-0 text-xs min-w-[42px] justify-center rounded-md bg-amber-500/20 text-amber-600 dark:text-amber-300 border border-amber-500/40 hover:bg-amber-500/25 font-bold tabular-nums">
        {event.time_elapsed}'
      </Badge>
      <div className="relative flex-shrink-0 mt-0.5" style={{ filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.5))' }}>
        <Zap className="w-4 h-4 text-amber-500 fill-amber-500/40" />
      </div>
      <div className={`relative flex-1 min-w-0 ${isAway ? 'text-right' : ''}`}>
        <div className="flex items-center gap-1.5" style={{ justifyContent: isAway ? 'flex-end' : 'flex-start' }}>
          <Badge className="text-[10px] py-0 px-1.5 h-4 rounded bg-amber-600 dark:bg-amber-500 text-white border-0 hover:bg-amber-600 dark:hover:bg-amber-500 font-bold tracking-wide">
            SG7
          </Badge>
          <span className="text-xs font-semibold text-amber-600 dark:text-amber-300 tracking-wide">Live Insight</span>
        </div>
        <p className="text-xs text-foreground/70 mt-1 font-medium leading-snug">
          {message}
        </p>
      </div>
    </div>
  );
}

export function InsightConfirmRow({ event, side }: InsightEventRowProps) {
  const isAway = side === 'away';
  const minutesAgo = event.minutes_ago ?? 0;
  const minuteText = minutesAgo <= 1 ? '1 minute' : `${minutesAgo} minutes`;

  const message = event.is_late
    ? `SG7 alert — ${event.team_name} have found the goal.`
    : `⚽ SG7 flagged it ${minuteText} ago. ${event.team_name} scored!`;

  return (
    <div className={`relative flex items-start gap-2.5 py-3.5 px-3 overflow-hidden ${isAway ? 'flex-row-reverse' : ''}`}>
      {/* Gradient side border */}
      <div className={`absolute top-0 bottom-0 w-[3px] bg-gradient-to-b from-emerald-400 to-emerald-600 ${isAway ? 'right-0' : 'left-0'}`} />
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/[0.08] via-emerald-500/[0.04] to-transparent pointer-events-none" />
      {/* Gradient bottom separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-emerald-500/30 via-emerald-500/15 to-transparent" />

      <Badge className="relative flex-shrink-0 text-xs min-w-[42px] justify-center rounded-md bg-emerald-500/20 text-emerald-600 dark:text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25 font-bold">
        <CheckCircle2 className="w-3.5 h-3.5" />
      </Badge>
      <div className="relative flex-shrink-0 mt-0.5" style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.5))' }}>
        <Zap className="w-4 h-4 text-emerald-500 fill-emerald-500/40" />
      </div>
      <div className={`relative flex-1 min-w-0 ${isAway ? 'text-right' : ''}`}>
        <div className="flex items-center gap-1.5" style={{ justifyContent: isAway ? 'flex-end' : 'flex-start' }}>
          <Badge className="text-[10px] py-0 px-1.5 h-4 rounded bg-emerald-600 dark:bg-emerald-500 text-white border-0 hover:bg-emerald-600 dark:hover:bg-emerald-500 font-bold tracking-wide">
            SG7
          </Badge>
          <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-300 tracking-wide">Confirmed</span>
        </div>
        <p className="text-xs text-foreground/70 mt-1 font-medium leading-snug">
          {message}
        </p>
      </div>
    </div>
  );
}

export function InsightVarDisallowedRow({ event, side, varDetail }: InsightVarDisallowedRowProps) {
  const isAway = side === 'away';
  const msgIndex = Math.abs(event.id.charCodeAt(event.id.length - 1)) % VAR_MESSAGES.length;
  const message = VAR_MESSAGES[msgIndex](event.team_name);

  return (
    <div className={`relative flex items-start gap-2.5 py-3.5 px-3 overflow-hidden ${isAway ? 'flex-row-reverse' : ''}`}>
      {/* Gradient side border */}
      <div className={`absolute top-0 bottom-0 w-[3px] bg-gradient-to-b from-rose-400 to-rose-600 ${isAway ? 'right-0' : 'left-0'}`} />
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-rose-500/[0.08] via-rose-500/[0.04] to-transparent pointer-events-none" />
      {/* Gradient bottom separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-rose-500/30 via-rose-500/15 to-transparent" />

      <Badge className="relative flex-shrink-0 text-xs min-w-[42px] justify-center rounded-md bg-rose-500/20 text-rose-600 dark:text-rose-300 border border-rose-500/40 hover:bg-rose-500/25 font-bold">
        VAR
      </Badge>
      <div className="relative flex-shrink-0 mt-0.5" style={{ filter: 'drop-shadow(0 0 4px rgba(244, 63, 94, 0.5))' }}>
        <ShieldOff className="w-4 h-4 text-rose-500" />
      </div>
      <div className={`relative flex-1 min-w-0 ${isAway ? 'text-right' : ''}`}>
        <div className="flex items-center gap-1.5" style={{ justifyContent: isAway ? 'flex-end' : 'flex-start' }}>
          <Badge className="text-[10px] py-0 px-1.5 h-4 rounded bg-rose-600 dark:bg-rose-500 text-white border-0 hover:bg-rose-600 dark:hover:bg-rose-500 font-bold tracking-wide">
            SG7
          </Badge>
          <span className="text-xs font-semibold text-rose-600 dark:text-rose-300 tracking-wide">Overturned</span>
        </div>
        <p className="text-xs text-foreground/70 mt-1 font-medium leading-snug">
          {message}
        </p>
      </div>
    </div>
  );
}
